import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchAdminDashboard } from '../../store/slices/adminSlice';
import {
  Grid, Card, CardContent, Typography, Box, CircularProgress, Paper
} from '@mui/material';
import {
  Business, People, Inventory, ShoppingCart, Warning,
  AccountBalance, TrendingUp, AttachMoney
} from '@mui/icons-material';

const statIcons = {
  totalSuppliers: <Business sx={{ fontSize: 40, color: '#1565c0' }} />,
  totalCustomers: <People sx={{ fontSize: 40, color: '#2e7d32' }} />,
  totalProducts: <Inventory sx={{ fontSize: 40, color: '#e65100' }} />,
  totalOrders: <ShoppingCart sx={{ fontSize: 40, color: '#6a1b9a' }} />,
  activeViolations: <Warning sx={{ fontSize: 40, color: '#c62828' }} />,
  pendingApprovals: <Inventory sx={{ fontSize: 40, color: '#f57f17' }} />,
  totalRevenue: <AttachMoney sx={{ fontSize: 40, color: '#00695c' }} />,
  activeEscrows: <AccountBalance sx={{ fontSize: 40, color: '#283593' }} />,
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dashboard, loading } = useSelector(state => state.admin);

  useEffect(() => {
    dispatch(fetchAdminDashboard());
  }, [dispatch]);

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  const stats = [
    { key: 'totalSuppliers', label: t('manage_suppliers'), value: dashboard?.totalSuppliers || 0 },
    { key: 'totalCustomers', label: 'Total Customers', value: dashboard?.totalCustomers || 0 },
    { key: 'totalProducts', label: t('manage_products'), value: dashboard?.totalProducts || 0 },
    { key: 'totalOrders', label: t('manage_orders'), value: dashboard?.totalOrders || 0 },
    { key: 'pendingApprovals', label: t('content_approval'), value: dashboard?.pendingApprovals || 0 },
    { key: 'activeViolations', label: t('violations'), value: dashboard?.activeViolations || 0 },
    { key: 'totalRevenue', label: 'Total Revenue', value: `$${(dashboard?.totalRevenue || 0).toLocaleString()}` },
    { key: 'activeEscrows', label: 'Active Escrows', value: dashboard?.activeEscrows || 0 },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>{t('admin_dashboard')}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome to the OSESG Admin Panel. Here's an overview of your platform.
      </Typography>

      <Grid container spacing={3}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.key}>
            <Card sx={{
              '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 4 }
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>{statIcons[stat.key]}</Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Recent Orders</Typography>
            {dashboard?.recentOrders?.length > 0 ? dashboard.recentOrders.map((order, i) => {
              const amount = order.pricing?.total ?? order.totalAmount ?? 0;
              const currency = order.pricing?.currency || 'USD';
              return (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee', gap: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{order.orderNumber}</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 110, textAlign: 'right' }}>
                    {amount.toLocaleString(undefined, { style: 'currency', currency })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
                    {order.status}
                  </Typography>
                </Box>
              );
            }) : <Typography variant="body2" color="text.secondary">No recent orders</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>System Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">API Server</Typography>
                <Typography variant="body2" color="success.main" fontWeight={600}>Online</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Database</Typography>
                <Typography variant="body2" color="success.main" fontWeight={600}>Connected</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">AI Services</Typography>
                <Typography variant="body2" color="success.main" fontWeight={600}>Active</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
